import {
  QUALIFICATION_CODE,
  RANGE_QUALIFICATION_CODES,
  QuotaCriteria,
  rangeUnitsForQualification,
} from '@ps/survey-utils';
import { nonEmptyList } from '@ps/utils';
import { Option } from '@sapphire/result';

import {
  FeasibilityDefaultQuotaCriteriaDto,
  FeasibilityRangeSetDto,
} from '../dtos/feasibility.dto';

/** The drawer stores a band as scalar rangeMin/rangeMax and never fills rangeSets. */
export function rangeSetsFor(
  criterion: QuotaCriteria,
): Option<FeasibilityRangeSetDto[]> {
  return nonEmptyList(criterion.rangeSets)
    .orElse(() => _rangeSetFromBounds(criterion))
    .map((ranges) =>
      _rangeSetsWithResolvedUnits(criterion.qualificationCode, ranges),
    );
}

/** The same unit rule, for a default-quota criterion already in payload wire shape. */
export function withResolvedUnitsForPayload(
  criterion: FeasibilityDefaultQuotaCriteriaDto,
): FeasibilityDefaultQuotaCriteriaDto {
  return nonEmptyList(criterion.range_sets).mapOr(criterion, (ranges) => ({
    ...criterion,
    range_sets: _rangeSetsWithResolvedUnits(
      criterion.qualification_code,
      ranges,
    ),
  }));
}

/** ZIPCODE uploads run to tens of thousands of entries and `defaultQuota` is display-only,
 *  so neither codes nor names ship there. Emptied, not removed — an absent key would read
 *  as "no conditions at all". */
export function withoutZipcodeConditions(
  criterion: FeasibilityDefaultQuotaCriteriaDto,
): FeasibilityDefaultQuotaCriteriaDto {
  return criterion.qualification_code === QUALIFICATION_CODE.ZIPCODE
    ? { ...criterion, condition_codes: [], condition_names: [] }
    : criterion;
}

/** The drawer's blank entry row holds header bounds, not a target, so it never ships. */
export function targetsAnything(criterion: QuotaCriteria): boolean {
  return (
    nonEmptyList(criterion.conditionCodes).isSome() ||
    rangeSetsFor(criterion).isSome()
  );
}

function _rangeSetFromBounds(
  criterion: QuotaCriteria,
): Option<readonly FeasibilityRangeSetDto[]> {
  return Option.all([
    Option.from(criterion.rangeMin),
    Option.from(criterion.rangeMax),
  ]).map(([from, to]) => [{ from, to, units: 0 }]);
}

/** A plain range qualification's unit is left at 0 upstream, so it is decided from the
 *  qualification code — see PLAIN_RANGE_QUALIFICATION_CODES. */
function _rangeSetsWithResolvedUnits(
  qualificationCode: number,
  ranges: readonly FeasibilityRangeSetDto[],
): FeasibilityRangeSetDto[] {
  return RANGE_QUALIFICATION_CODES.has(qualificationCode)
    ? ranges.map((range) => ({
        ...range,
        units: rangeUnitsForQualification(qualificationCode),
      }))
    : [...ranges];
}
